<script setup lang="ts">
import { currentDeckYear } from '@open-songster/core'
import { DECK_START_YEAR } from '@open-songster/shared'
import { computed, ref, watch } from 'vue'

interface Props {
  from: number
  to: number
}

const props = defineProps<Props>()
const emit = defineEmits<{ change: [from: number, to: number]; reset: [] }>()

const maxYear = currentDeckYear()
const localFrom = ref(props.from)
const localTo = ref(props.to)

watch(
  () => [props.from, props.to] as const,
  ([from, to]) => {
    localFrom.value = from
    localTo.value = to
  },
)

const isDefault = computed(() => props.from === DECK_START_YEAR && props.to === maxYear)

function clamp(value: number): number {
  if (!Number.isFinite(value)) return DECK_START_YEAR
  return Math.min(Math.max(Math.round(value), DECK_START_YEAR), maxYear)
}

/** A backwards range is a slip, not an error state: swap it silently. */
function commit(): void {
  const from = clamp(localFrom.value)
  const to = clamp(localTo.value)
  const ordered: [number, number] = from <= to ? [from, to] : [to, from]
  localFrom.value = ordered[0]
  localTo.value = ordered[1]
  emit('change', ordered[0], ordered[1])
}
</script>

<template>
  <div class="panel panel--inset stack">
    <div class="spread">
      <strong>{{ $t('playlists.filterTitle') }}</strong>
      <button
        v-if="!isDefault"
        type="button"
        class="btn btn--ghost"
        @click="emit('reset')"
      >
        {{ $t('playlists.filterReset') }}
      </button>
    </div>
    <div class="row" style="gap: var(--space-3)">
      <label class="field">
        <span class="label">{{ $t('common.from') }}</span>
        <input
          v-model.number="localFrom"
          class="input"
          type="number"
          inputmode="numeric"
          :min="DECK_START_YEAR"
          :max="maxYear"
          @change="commit"
        />
      </label>
      <label class="field">
        <span class="label">{{ $t('common.to') }}</span>
        <input
          v-model.number="localTo"
          class="input"
          type="number"
          inputmode="numeric"
          :min="DECK_START_YEAR"
          :max="maxYear"
          @change="commit"
        />
      </label>
    </div>
    <p class="hint">{{ $t('playlists.filterHint') }}</p>
  </div>
</template>
